import { useState, type ReactNode, type UIEvent } from 'react';
import { Link } from 'react-router-dom';
import { BadgePercent, Clock, ExternalLink, MapPin, Phone, Route, Tag, Ticket, type LucideIcon } from 'lucide-react';
import {
  formatDistance,
  formatTourDuration,
  formatTourPrice,
  tourDiscountLabel,
  type GuestPlace,
  type GuestTourPackage,
  type PlacesToExploreImageRef,
} from '@hotel-voice/shared';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { ExploreImage } from './ExploreImage';

/* -------------------------------------------------------------------------- */
/* Shared pieces                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A grid tile, two to a row on a phone. `h-full` + a flex column so tiles in
 * one row match heights however long each name runs, with the price (or
 * distance) pinned to the bottom edge.
 */
const CARD =
  'bg-card flex h-full w-full min-w-0 flex-col overflow-hidden rounded-2xl border text-left shadow-xs transition-transform active:scale-[0.98] outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]';

/** A frosted label laid over a photo — readable on any image. Sized for a half-width tile. */
function PhotoPill({ icon: Icon, children, className }: { icon: LucideIcon; children: string; className?: string }) {
  return (
    <span
      className={cn(
        'absolute flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm',
        className,
      )}
    >
      <Icon className="size-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{children}</span>
    </span>
  );
}

/**
 * Every photo, full width, swiped sideways with a counter. A single photo is
 * just the photo; none is the fallback tile, so the sheet keeps its shape.
 */
function Gallery({ images, alt, icon }: { images: PlacesToExploreImageRef[]; alt: string; icon: LucideIcon }) {
  const [index, setIndex] = useState(0);

  if (images.length <= 1) {
    return <ExploreImage path={images[0]?.path} alt={alt} icon={icon} className="aspect-[4/3] w-full" />;
  }

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    setIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <div className="relative">
      <div
        onScroll={handleScroll}
        className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label={`${alt} photos`}
      >
        {images.map((image, i) => (
          <ExploreImage
            key={image.path}
            path={image.path}
            alt={`${alt} — photo ${i + 1} of ${images.length}`}
            icon={icon}
            className="aspect-[4/3] w-full shrink-0 snap-center"
          />
        ))}
      </div>
      <span className="absolute right-3 bottom-3 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white">
        {index + 1} / {images.length}
      </span>
    </div>
  );
}

/** The detail sheet's frame: gallery flush to the top, scrolling body, close button above the photo. */
function DetailSheet({
  open,
  onOpenChange,
  gallery,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gallery: ReactNode;
  children: ReactNode;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="gap-0 overflow-y-auto p-0 [&>button:last-child]:rounded-full [&>button:last-child]:bg-black/55 [&>button:last-child]:p-1.5 [&>button:last-child]:text-white [&>button:last-child]:opacity-100"
      >
        <div className="overflow-hidden rounded-t-3xl">{gallery}</div>
        <div className="flex flex-col gap-4 px-5 pt-5">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

/* -------------------------------------------------------------------------- */
/* Places                                                                     */
/* -------------------------------------------------------------------------- */

function distanceText(place: GuestPlace) {
  return `${formatDistance(place.distance)} from hotel`;
}

function mapsUrl(place: GuestPlace) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${place.name}, ${place.locationDetails}`,
  )}`;
}

/**
 * Half-width tile: the photo with the distance on it, then name, location and
 * a two-line teaser. The tile says "8 km"; the sheet says "8 km from hotel" —
 * the longer form does not fit beside a photo this narrow.
 */
export function PlaceCard({ place, onOpen }: { place: GuestPlace; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className={CARD}>
      <div className="relative">
        <ExploreImage path={place.images[0]?.path} alt={place.name} icon={MapPin} className="aspect-[4/3] w-full" />
        <PhotoPill icon={Route} className="bottom-2 left-2">
          {formatDistance(place.distance)}
        </PhotoPill>
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h3 className="line-clamp-2 text-[15px] leading-snug font-bold">{place.name}</h3>
        <p className="text-muted-foreground flex min-w-0 items-center gap-1 text-xs">
          <MapPin className="text-primary size-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{place.locationDetails}</span>
        </p>
        {place.shortDescription && (
          <p className="text-foreground/75 mt-0.5 line-clamp-2 text-xs leading-relaxed">{place.shortDescription}</p>
        )}
      </div>
    </button>
  );
}

export function PlaceDetail({
  place,
  onOpenChange,
}: {
  place: GuestPlace | null;
  onOpenChange: (open: boolean) => void;
}) {
  // Keeps the last place while the sheet animates closed.
  const [shown, setShown] = useState<GuestPlace | null>(place);
  if (place && place !== shown) setShown(place);
  if (!shown) return null;

  return (
    <DetailSheet
      open={place !== null}
      onOpenChange={onOpenChange}
      gallery={<Gallery images={shown.images} alt={shown.name} icon={MapPin} />}
    >
      <SheetHeader className="gap-2 p-0 text-left">
        <SheetTitle className="text-2xl leading-tight font-bold">{shown.name}</SheetTitle>
        <SheetDescription asChild>
          <div className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted-foreground flex items-start gap-1.5">
              <MapPin className="text-primary mt-0.5 size-4 shrink-0" aria-hidden="true" />
              {shown.locationDetails}
            </span>
            <span className="text-foreground flex items-center gap-1.5 font-medium">
              <Route className="text-primary size-4 shrink-0" aria-hidden="true" />
              {distanceText(shown)}
            </span>
          </div>
        </SheetDescription>
      </SheetHeader>

      {shown.shortDescription && (
        <p className="text-foreground/85 text-[15px] leading-relaxed whitespace-pre-line">{shown.shortDescription}</p>
      )}

      <div className="flex flex-col gap-2 pb-2">
        <Button asChild size="lg">
          <a href={mapsUrl(shown)} target="_blank" rel="noopener noreferrer">
            <ExternalLink aria-hidden="true" />
            Open in Maps
          </a>
        </Button>
        <Button asChild size="lg" variant="outline">
          <Link to="/call">
            <Phone aria-hidden="true" />
            Ask reception for directions
          </Link>
        </Button>
      </div>
    </DetailSheet>
  );
}

/* -------------------------------------------------------------------------- */
/* Tour packages                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Original price struck through, the discount, then the final price large —
 * the number a guest acts on is the one that stands out. Without a discount
 * there is only the one price, at the same size.
 */
function PriceBlock({ pkg, size = 'md' }: { pkg: GuestTourPackage; size?: 'md' | 'lg' }) {
  const discount = tourDiscountLabel(pkg);
  return (
    <div className="flex flex-wrap items-end gap-x-2.5 gap-y-1">
      <span className={cn('text-success font-extrabold tracking-tight', size === 'lg' ? 'text-3xl' : 'text-2xl')}>
        {formatTourPrice(pkg.finalPrice)}
      </span>
      {discount && (
        <>
          <span className="text-muted-foreground pb-0.5 text-sm line-through" aria-label="Original price">
            {formatTourPrice(pkg.price)}
          </span>
          <span className="bg-success/15 text-success mb-0.5 rounded-full px-2 py-0.5 text-xs font-bold">{discount}</span>
        </>
      )}
      <span className="text-muted-foreground w-full text-xs">per package</span>
    </div>
  );
}

/**
 * Half-width tile: photo with the discount badge (top) and duration (bottom),
 * name, a two-line teaser, and the price pinned to the tile's foot — final
 * price large in green, the original struck through beneath it.
 */
export function TourCard({ pkg, onOpen }: { pkg: GuestTourPackage; onOpen: () => void }) {
  const discount = tourDiscountLabel(pkg);
  return (
    <button type="button" onClick={onOpen} className={CARD}>
      <div className="relative">
        <ExploreImage path={pkg.images[0]?.path} alt={pkg.name} icon={Ticket} className="aspect-[4/3] w-full" />
        {discount && (
          <span className="bg-success absolute top-2 right-2 flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow-sm">
            <BadgePercent className="size-3" aria-hidden="true" />
            {discount}
          </span>
        )}
        {pkg.duration && (
          <PhotoPill icon={Clock} className="bottom-2 left-2">
            {formatTourDuration(pkg.duration)}
          </PhotoPill>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h3 className="line-clamp-2 text-[15px] leading-snug font-bold">{pkg.name}</h3>
        <p className="text-foreground/75 line-clamp-2 text-xs leading-relaxed">{pkg.details}</p>
        <div className="mt-auto pt-2">
          <p className="text-success flex items-center gap-1 text-lg leading-tight font-extrabold tracking-tight">
            <Tag className="size-3.5 shrink-0" aria-hidden="true" />
            {formatTourPrice(pkg.finalPrice)}
          </p>
          {discount && (
            <p className="text-muted-foreground text-xs line-through" aria-label="Original price">
              {formatTourPrice(pkg.price)}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

export function TourDetail({
  pkg,
  onOpenChange,
}: {
  pkg: GuestTourPackage | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [shown, setShown] = useState<GuestTourPackage | null>(pkg);
  if (pkg && pkg !== shown) setShown(pkg);
  if (!shown) return null;

  return (
    <DetailSheet
      open={pkg !== null}
      onOpenChange={onOpenChange}
      gallery={<Gallery images={shown.images} alt={shown.name} icon={Ticket} />}
    >
      <SheetHeader className="gap-1 p-0 text-left">
        <SheetTitle className="text-2xl leading-tight font-bold">{shown.name}</SheetTitle>
        {shown.duration ? (
          <SheetDescription className="text-foreground flex items-center gap-1.5 text-sm font-medium">
            <Clock className="text-primary size-4 shrink-0" aria-hidden="true" />
            Duration: {formatTourDuration(shown.duration)}
          </SheetDescription>
        ) : (
          <SheetDescription className="sr-only">Tour package details and price</SheetDescription>
        )}
      </SheetHeader>

      <div className="bg-muted/60 rounded-2xl p-4">
        <PriceBlock pkg={shown} size="lg" />
      </div>

      <p className="text-foreground/85 text-[15px] leading-relaxed whitespace-pre-line">{shown.details}</p>

      <div className="flex flex-col gap-2 pb-2">
        <Button asChild size="lg">
          <Link to="/call">
            <Phone aria-hidden="true" />
            Call reception to book
          </Link>
        </Button>
        <p className="text-muted-foreground text-center text-xs">
          Reception will confirm availability, timings and pickup.
        </p>
      </div>
    </DetailSheet>
  );
}
